import React, { useMemo } from "react"
import {
  toCurrency,
  toNumber,
  convertNumber,
} from "~/ui-rtk/utils/number-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  computeWowGrowthValues,
  type WowGrowthColumn,
  type WowGrowthRowData,
} from "./wow-growth"

export interface WowGrowthRowMetric extends WowGrowthRowData {
  metric?: string
  type?: string
  suffix?: string
}

// An inline period-over-period growth row rendered directly beneath the metric
// it tracks (custom reports only). It shows the absolute delta formatted in the
// metric's own type plus the percent change, and labels itself with the
// granularity term (e.g. "WoW Growth") over the metric's name so it's clear
// which metric is being tracked. Kept free of the charts/dayjs import chain so
// it can be rendered in isolation.
const WowGrowthRow: React.FC<{
  row: WowGrowthRowMetric
  columns: WowGrowthColumn[]
  label: string
}> = ({ row, columns, label }) => {
  const values = useMemo(
    () => computeWowGrowthValues(row, columns),
    [row, columns],
  )
  const type = row.type
  const suffix = row.suffix || ""

  const formatDelta = (value: number): string | number => {
    if (type === "currency") {
      // toCurrency renders "< $0.01" for a zero/near-zero positive, which reads
      // wrong for an exact no-change delta.
      return value === 0 ? "$0.00" : toCurrency(value, "--", false, true)
    }
    // The catalog uses both "percentage" and "percent" for point-valued metrics
    // (e.g. conversion rate); render the delta in points with a % sign for both.
    if (type === "percentage" || type === "percent") {
      return `${toNumber(value, "--", { fractionDigits: 2 })}%`
    }
    if (type === "decimal") {
      return convertNumber("decimal", value)
    }
    return toNumber(value, "--", { fractionDigits: 0 })
  }

  const toneClass = (value: number) => {
    if (value > 0) return "text-system-success-400"
    if (value < 0) return "text-system-error-400"
    return "text-system-gray-400"
  }

  const arrow = (value: number) => {
    if (value > 0) return "↗"
    if (value < 0) return "↘"
    return "="
  }

  return (
    <tr className="bg-system-blue-800/40">
      {columns.map((column, colIndex) => {
        if (colIndex === 0) {
          return (
            <td
              key={`growth-${column.key}`}
              className={cn(
                "text-sm text-white p-0 sticky left-0 bg-system-blue-800",
                "border-r border-system-blue-600 w-60 min-w-60 max-w-60",
              )}
            >
              <div className="flex flex-col items-start leading-tight px-6 py-2">
                <span className="text-sm font-medium text-white italic">
                  {label}
                </span>
                <span className="text-xs text-system-blue-300 truncate max-w-[13rem]">
                  {row.metric}
                </span>
              </div>
            </td>
          )
        }

        const cell = values[column.key]
        return (
          <td
            key={`growth-${column.key}`}
            className="px-6 py-2 text-center whitespace-nowrap text-sm w-32 min-w-32 max-w-32"
          >
            {cell ? (
              <div className="leading-tight">
                <div
                  className={cn(
                    "font-mono text-sm italic",
                    toneClass(cell.delta),
                  )}
                >
                  {cell.delta > 0 ? "+" : ""}
                  {formatDelta(cell.delta)}
                  {suffix}
                </div>
                <div
                  className={cn(
                    "flex items-center justify-center gap-0.5 text-xs font-medium",
                    toneClass(cell.delta),
                  )}
                >
                  {cell.pct === null ? (
                    <span className="text-system-blue-400">—</span>
                  ) : (
                    <>
                      <span>{arrow(cell.delta)}</span>
                      <span className="font-mono">
                        {Math.abs(cell.pct).toFixed(1)}%
                      </span>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-system-blue-400 text-sm">—</span>
            )}
          </td>
        )
      })}
    </tr>
  )
}

export default WowGrowthRow
